import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { applicationsAPI, jobsAPI } from '../../services/api';
import BusinessLayout from '../../components/BusinessLayout';
import ConfirmModal from '../../components/ConfirmModal';
import toast from 'react-hot-toast';
import { 
  Users, 
  Search, 
  Filter, 
  Eye, 
  Star, 
  CheckCircle, 
  XCircle, 
  Clock,
  Mail,
  Phone,
  Download,
  MoreVertical,
  Briefcase,
  MapPin,
  Calendar
} from 'lucide-react';

interface Application {
  _id: string;
  job: {
    _id: string;
    title: string;
    interviewRequired?: boolean;
  };
  applicant: {
    user: {
      firstName: string;
      lastName: string;
      email: string;
      phone?: string;
    };
    title?: string;
    location?: string;
    skills?: string[];
    experience?: string;
  };
  status: string;
  coverLetter?: string;
  documents?: {
    cv?: string;
    portfolio?: string;
  };
  createdAt: string;
}

const EmployerApplications: React.FC = () => {
  const [applications, setApplications] = useState<Application[]>([]);
  const [jobs, setJobs] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [jobFilter, setJobFilter] = useState('');
  const [selectedApp, setSelectedApp] = useState<Application | null>(null);
  const [showDetail, setShowDetail] = useState(false);
  const [confirmModal, setConfirmModal] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    type: 'danger' | 'warning' | 'info' | 'success';
    confirmText?: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: '',
    message: '',
    type: 'danger',
    onConfirm: () => {}
  });
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [statusFilter, jobFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const params: any = {};
      if (statusFilter) params.status = statusFilter;
      if (jobFilter) params.jobId = jobFilter;

      const [appsRes, jobsRes] = await Promise.all([
        applicationsAPI.getMyJobApplications(params),
        jobsAPI.getMyJobs()
      ]);

      setApplications(appsRes.data.data || []);
      setJobs(jobsRes.data.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredApps = applications.filter(app => {
    if (!search) return true;
    const fullName = `${app.applicant?.user?.firstName} ${app.applicant?.user?.lastName}`.toLowerCase();
    return fullName.includes(search.toLowerCase()) || 
           app.applicant?.user?.email?.toLowerCase().includes(search.toLowerCase()) ||
           app.job?.title?.toLowerCase().includes(search.toLowerCase());
  });

  const handleStatusChange = (appId: string, newStatus: string) => {
    const app = applications.find(a => a._id === appId);
    const statusLabels: Record<string, string> = {
      submitted: 'Under Review',
      reviewing: 'Shortlist',
      shortlisted: 'Interview',
      interviewed: 'Offer',
      offered: 'Hire',
      rejected: 'Reject'
    };

    setConfirmModal({
      isOpen: true,
      title: `${statusLabels[newStatus] || newStatus} Candidate`,
      message: `Are you sure you want to mark this candidate as ${newStatus}?`,
      type: newStatus === 'rejected' ? 'danger' : newStatus === 'offered' || newStatus === 'hired' ? 'success' : 'info',
      confirmText: statusLabels[newStatus] || newStatus,
      onConfirm: async () => {
        setProcessing(true);
        try {
          await applicationsAPI.updateStatus(appId, { status: newStatus });
          toast.success(`Candidate marked as ${newStatus}`);
          setConfirmModal({ ...confirmModal, isOpen: false });
          fetchData();
        } catch (error) {
          toast.error('Failed to update status');
        } finally {
          setProcessing(false);
        }
      }
    });
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'submitted': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'reviewing': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'shortlisted': return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'interviewed': return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'offered': return 'bg-green-100 text-green-700 border-green-200';
      case 'hired': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'rejected': return 'bg-red-100 text-red-700 border-red-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  return (
    <BusinessLayout>
      <div className="p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Applications</h1>
            <p className="text-gray-500 mt-1">Review and manage all job applications</p>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-500">{filteredApps.length} applications</span>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 mb-6">
          <div className="flex flex-wrap items-center gap-4">
            <div className="relative flex-1 min-w-[200px]">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search candidates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Status</option>
              <option value="submitted">Submitted</option>
              <option value="reviewing">Reviewing</option>
              <option value="shortlisted">Shortlisted</option>
              <option value="interviewed">Interviewed</option>
              <option value="offered">Offered</option>
              <option value="hired">Hired</option>
              <option value="rejected">Rejected</option>
            </select>
            <select
              value={jobFilter}
              onChange={(e) => setJobFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Jobs</option>
              {jobs.map(job => (
                <option key={job._id} value={job._id}>{job.title}</option>
              ))}
            </select>
          </div>
        </div>

        {/* Applications List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-12 text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          ) : filteredApps.length === 0 ? (
            <div className="p-12 text-center">
              <Users className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No applications found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Candidate</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Job</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Applied</th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredApps.map((app) => (
                    <tr key={app._id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <span className="text-blue-600 font-medium">
                              {app.applicant?.user?.firstName?.[0]}{app.applicant?.user?.lastName?.[0]}
                            </span>
                          </div>
                          <div>
                            <p className="font-medium text-gray-900">
                              {app.applicant?.user?.firstName} {app.applicant?.user?.lastName}
                            </p>
                            <p className="text-sm text-gray-500">{app.applicant?.user?.email}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-gray-900">{app.job?.title}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded-full text-xs font-medium border ${getStatusColor(app.status)}`}>
                          {app.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        {new Date(app.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center justify-end gap-2">
                          <button
                            onClick={() => {
                              setSelectedApp(app);
                              setShowDetail(true);
                            }}
                            className="p-2 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                            title="View Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          
                          {/* Shortlist - from submitted/reviewing */}
                          {(app.status === 'submitted' || app.status === 'reviewing') && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'shortlisted')}
                              className="p-2 text-gray-400 hover:text-yellow-600 hover:bg-yellow-50 rounded-lg transition-colors"
                              title="Shortlist"
                            >
                              <Star className="w-4 h-4" />
                            </button>
                          )}

                          {/* Direct Hire - skip interview if not required */}
                          {(app.status === 'submitted' || app.status === 'reviewing' || app.status === 'shortlisted') && 
                           app.job?.interviewRequired === false && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'hired')}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Direct Hire"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}

                          {/* Interview - only if required */}
                          {app.status === 'shortlisted' && app.job?.interviewRequired !== false && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'interviewed')}
                              className="p-400 hover:text-2 text-gray-indigo-600 hover:bg-indigo-50 rounded-lg transition-colors"
                              title="Schedule Interview"
                            >
                              <Clock className="w-4 h-4" />
                            </button>
                          )}

                          {/* Make Offer - after interview */}
                          {(app.status === 'interviewed' || (app.status === 'shortlisted' && app.job?.interviewRequired === false)) && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'offered')}
                              className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                              title="Make Offer"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}

                          {/* Hire - after offer */}
                          {app.status === 'offered' && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'hired')}
                              className="p-2 text-gray-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition-colors"
                              title="Confirm Hire"
                            >
                              <CheckCircle className="w-4 h-4" />
                            </button>
                          )}

                          {/* Reject - always available except if already rejected/hired */}
                          {app.status !== 'rejected' && app.status !== 'hired' && (
                            <button
                              onClick={() => handleStatusChange(app._id, 'rejected')}
                              className="p-2 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                              title="Reject"
                            >
                              <XCircle className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Detail Modal */}
        {showDetail && selectedApp && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between">
                  <h3 className="text-xl font-semibold text-gray-900">Application Details</h3>
                  <button
                    onClick={() => {
                      setShowDetail(false);
                      setSelectedApp(null);
                    }}
                    className="p-2 hover:bg-gray-100 rounded-lg"
                  >
                    <XCircle className="w-5 h-5 text-gray-400" />
                  </button>
                </div>
              </div>
              <div className="p-6">
                {/* Candidate Info */}
                <div className="flex items-center gap-4 mb-6">
                  <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-600 font-bold text-xl">
                      {selectedApp.applicant?.user?.firstName?.[0]}{selectedApp.applicant?.user?.lastName?.[0]}
                    </span>
                  </div>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900">
                      {selectedApp.applicant?.user?.firstName} {selectedApp.applicant?.user?.lastName}
                    </h4>
                    <p className="text-gray-500">{selectedApp.applicant?.title || 'Candidate'}</p>
                  </div>
                </div>

                {/* Contact Info */}
                <div className="grid grid-cols-2 gap-4 mb-6">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Mail className="w-4 h-4" />
                    <span className="text-sm">{selectedApp.applicant?.user?.email}</span>
                  </div>
                  {selectedApp.applicant?.user?.phone && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-4 h-4" />
                      <span className="text-sm">{selectedApp.applicant?.user?.phone}</span>
                    </div>
                  )}
                  {selectedApp.applicant?.location && (
                    <div className="flex items-center gap-2 text-gray-600">
                      <MapPin className="w-4 h-4" />
                      <span className="text-sm">{selectedApp.applicant?.location}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-gray-600">
                    <Briefcase className="w-4 h-4" />
                    <span className="text-sm">{selectedApp.job?.title}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600">
                    <Calendar className="w-4 h-4" />
                    <span className="text-sm">Applied: {new Date(selectedApp.createdAt).toLocaleDateString()}</span>
                  </div>
                  {selectedApp.job?.interviewRequired === false && (
                    <div className="flex items-center gap-2 text-green-600">
                      <CheckCircle className="w-4 h-4" />
                      <span className="text-sm font-medium">Direct hire enabled - No interview required</span>
                    </div>
                  )}
                </div>

                {/* Skills */}
                {selectedApp.applicant?.skills && selectedApp.applicant.skills.length > 0 && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Skills</h5>
                    <div className="flex flex-wrap gap-2">
                      {selectedApp.applicant.skills.map((skill, idx) => (
                        <span key={idx} className="px-2 py-1 bg-gray-100 text-gray-700 text-sm rounded">
                          {skill}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Cover Letter */}
                {selectedApp.coverLetter && (
                  <div className="mb-6">
                    <h5 className="text-sm font-medium text-gray-700 mb-2">Cover Letter</h5>
                    <div className="p-4 bg-gray-50 rounded-lg text-gray-600 text-sm">
                      {selectedApp.coverLetter}
                    </div>
                  </div>
                )}

                {/* Status Actions */}
                <div className="border-t border-gray-200 pt-6">
                  <h5 className="text-sm font-medium text-gray-700 mb-3">Update Status</h5>
                  <div className="flex flex-wrap gap-2">
                    {/* Shortlist */}
                    {(selectedApp.status === 'submitted' || selectedApp.status === 'reviewing') && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'shortlisted')}
                        className="px-4 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 text-sm"
                      >
                        Shortlist
                      </button>
                    )}

                    {/* Direct Hire - skip interview if not required */}
                    {(selectedApp.status === 'submitted' || selectedApp.status === 'reviewing' || selectedApp.status === 'shortlisted') && 
                     selectedApp.job?.interviewRequired === false && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'hired')}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm"
                      >
                        Hire Directly
                      </button>
                    )}

                    {/* Interview - only if required */}
                    {selectedApp.status === 'shortlisted' && selectedApp.job?.interviewRequired !== false && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'interviewed')}
                        className="px-4 py-2 bg-indigo-500 text-white rounded-lg hover:bg-indigo-600 text-sm"
                      >
                        Schedule Interview
                      </button>
                    )}

                    {/* Make Offer */}
                    {(selectedApp.status === 'interviewed' || (selectedApp.status === 'shortlisted' && selectedApp.job?.interviewRequired === false)) && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'offered')}
                        className="px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 text-sm"
                      >
                        Make Offer
                      </button>
                    )}

                    {/* Confirm Hire */}
                    {selectedApp.status === 'offered' && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'hired')}
                        className="px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 text-sm"
                      >
                        Confirm Hire
                      </button>
                    )}

                    {/* Reject */}
                    {selectedApp.status !== 'rejected' && selectedApp.status !== 'hired' && (
                      <button
                        onClick={() => handleStatusChange(selectedApp._id, 'rejected')}
                        className="px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 text-sm"
                      >
                        Reject
                      </button>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        <ConfirmModal
          isOpen={confirmModal.isOpen}
          onClose={() => setConfirmModal({ ...confirmModal, isOpen: false })}
          onConfirm={confirmModal.onConfirm}
          title={confirmModal.title}
          message={confirmModal.message}
          type={confirmModal.type}
          confirmText={confirmModal.confirmText}
          loading={processing}
        />
      </div>
    </BusinessLayout>
  );
};

export default EmployerApplications;
